import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Package, ShoppingCart, Truck } from 'lucide-react';
import { useToast } from './ui/use-toast';

export const Hero = () => {
  const { toast } = useToast();

  const handleComingSoon = (feature) => {
    toast({
      title: "📱 Application en développement",
      description: `${feature} sera bientôt disponible. Inscrivez-vous pour être notifié !`,
      duration: 4000,
    });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary-light to-secondary">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-glow rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Content */}
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-secondary-foreground animate-fade-in">
            <div className="inline-flex items-center space-x-2 bg-primary/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-primary">Plateforme E-commerce #1 au Bénin</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
              Transformez Votre
              <span className="block text-primary mt-2">Commerce Digital</span>
            </h1>

            <p className="text-lg sm:text-xl text-secondary-foreground/80 mb-8 leading-relaxed max-w-xl">
              Open Syne révolutionne l'e-commerce au Bénin avec des solutions innovantes. 
              Achetez local, commandez international, et profitez de services exclusifs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary-glow shadow-glow group"
                onClick={() => window.open('https://dev.vendeur.syneopen.com/#/apply', '_blank')}
              >
                Commencer Maintenant
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => handleComingSoon("L'application SYNE")}
              >
                Découvrir l'App SYNE
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-primary mb-1">500+</div>
                <div className="text-sm text-secondary-foreground/70">Marchands</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-primary mb-1">10K+</div>
                <div className="text-sm text-secondary-foreground/70">Utilisateurs</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-primary mb-1">98%</div>
                <div className="text-sm text-secondary-foreground/70">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="relative hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              {/* Card 1 */}
              <div className="glass p-6 rounded-2xl hover-lift">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <ShoppingCart className="text-secondary" size={24} />
                </div>
                <h3 className="font-semibold text-secondary-foreground mb-2">Achat Local</h3>
                <p className="text-sm text-secondary-foreground/70">Accès direct aux commerces béninois</p>
              </div>

              {/* Card 2 */}
              <div className="glass p-6 rounded-2xl hover-lift mt-8">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <Package className="text-secondary" size={24} />
                </div>
                <h3 className="font-semibold text-secondary-foreground mb-2">International</h3>
                <p className="text-sm text-secondary-foreground/70">Commandez sur Amazon, Alibaba...</p>
              </div>

              {/* Card 3 */}
              <div className="glass p-6 rounded-2xl hover-lift">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <Truck className="text-secondary" size={24} />
                </div>
                <h3 className="font-semibold text-secondary-foreground mb-2">Livraison</h3>
                <p className="text-sm text-secondary-foreground/70">Repas, pharmacie & colis sécurisés</p>
              </div>

              {/* Card 4 */}
              <div className="glass p-6 rounded-2xl hover-lift mt-8">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <svg className="text-secondary" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M9 3v18" />
                  </svg>
                </div>
                <h3 className="font-semibold text-secondary-foreground mb-2">Syne Drive</h3>
                <p className="text-sm text-secondary-foreground/70">Casiers sécurisés 24/7</p>
              </div>
            </div>

            {/* Floating element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/30 rounded-full blur-2xl animate-float"></div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-primary rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};